import { useState, useContext, useEffect } from 'react'
import styles from './GroupsTable.module.css'
import { SplitViewContext } from './ManageSection'
import apiClient from '../utils/api'
import AddGroupModal from './modals/AddGroupModal'
import { GroupDetails } from './details'

export default function GroupsTable({ apiToken, selectedGroupId, onSubSectionChange }) {
    const [searchTerm, setSearchTerm] = useState('')
    const [leaderFilter, setLeaderFilter] = useState('all')
    const [sortBy, setSortBy] = useState('name')
    const [sortOrder, setSortOrder] = useState('asc')
    const [showAddModal, setShowAddModal] = useState(false)
    const [selectedGroup, setSelectedGroup] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [leaders, setLeaders] = useState([])
    
    // Get split view context
    const splitViewContext = useContext(SplitViewContext)
    const { setIsInSplitView } = splitViewContext || {}
    
    // Update split view state when group selection changes
    useEffect(() => {
        if (setIsInSplitView) {
            setIsInSplitView(!!selectedGroup)
        }
    }, [selectedGroup, setIsInSplitView])

    // Load groups data from backend
    useEffect(() => {
        const loadGroups = async () => {
            if (!apiToken) return

            try {
                setLoading(true)
                setError(null)

                apiClient.setAuthToken(apiToken)

                // Build query parameters for filtering and search
                const queryParams = {
                    leader_id: leaderFilter === 'all' ? undefined : leaderFilter,
                    search: searchTerm || undefined,
                    sort_by: sortBy,
                    sort_order: sortOrder
                }

                // Remove undefined values
                const cleanParams = Object.fromEntries(
                    Object.entries(queryParams).filter(([_, value]) => value !== undefined)
                )

                const groupsResponse = await apiClient.getGroups(cleanParams)

                setGroups(groupsResponse.groups || [])
                
                // Extract unique leaders for filter dropdown
                const uniqueLeaders = []
                const leaderMap = new Map()
                
                groupsResponse.groups?.forEach(group => {
                    if (group.created_by && !leaderMap.has(group.created_by.id)) {
                        leaderMap.set(group.created_by.id, group.created_by)
                        uniqueLeaders.push(group.created_by)
                    }
                })
                
                setLeaders(uniqueLeaders)
            } catch (err) {
                console.error('Failed to load groups:', err)
                setError('Failed to load groups data')
            } finally {
                setLoading(false)
            }
        }

        loadGroups()
    }, [apiToken, leaderFilter, searchTerm, sortBy, sortOrder])

    const [groups, setGroups] = useState([])
    
    // Effect to automatically select group if selectedGroupId is provided
    useEffect(() => {
        if (groups.length > 0) {
            if (selectedGroupId) {
                // Only update if the selected group ID doesn't match the current selection
                if (!selectedGroup || selectedGroup.id !== selectedGroupId) {
                    const groupToSelect = groups.find(group => group.id === selectedGroupId)
                    if (groupToSelect) {
                        setSelectedGroup(groupToSelect)
                    }
                }
            } else {
                // If no selectedGroupId in URL, clear the selection
                if (selectedGroup) {
                    setSelectedGroup(null)
                }
            }
        }
    }, [selectedGroupId, groups])

    // Use groups directly since filtering is done on backend
    const filteredGroups = groups

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        })
    }

    const handleGroupSelect = (group) => {
        setSelectedGroup(group)
        // Update the URL to include the group ID
        if (onSubSectionChange) {
            onSubSectionChange('groups', null, group.id)
        }
    }

    const handleCloseDetails = () => {
        setSelectedGroup(null)
        // Update the URL to remove the group ID
        if (onSubSectionChange) {
            onSubSectionChange('groups')
        }
    }
    
    const handleGroupCreated = async (newGroup) => {
        // Add the new group to the list and refresh data
        try {
            // Reload groups data to ensure we have the complete data with member counts
            const queryParams = {
                leader_id: leaderFilter === 'all' ? undefined : leaderFilter,
                search: searchTerm || undefined,
                sort_by: sortBy,
                sort_order: sortOrder
            }

            // Remove undefined values
            const cleanParams = Object.fromEntries(
                Object.entries(queryParams).filter(([_, value]) => value !== undefined)
            )

            apiClient.setAuthToken(apiToken)
            const groupsResponse = await apiClient.getGroups(cleanParams)
            setGroups(groupsResponse.groups || [])
            
            // Update leaders list if necessary
            const uniqueLeaders = []
            const leaderMap = new Map()
            
            groupsResponse.groups?.forEach(group => {
                if (group.created_by && !leaderMap.has(group.created_by.id)) {
                    leaderMap.set(group.created_by.id, group.created_by)
                    uniqueLeaders.push(group.created_by)
                }
            })
            
            setLeaders(uniqueLeaders)
        } catch (err) {
            console.error('Failed to reload groups after creation:', err)
            // Fallback: just add the new group to the existing list
            setGroups(prevGroups => [...prevGroups, newGroup])
        }
        
        setShowAddModal(false)
    }

    
    const handleArchiveGroup = (group) => {
        // TODO: Implement archive group functionality
        console.log('Archive group:', group)
    }

    const handleGroupUpdate = async (updatedGroupId) => {
        if (!apiToken || !updatedGroupId) return

        try {
            apiClient.setAuthToken(apiToken)

            // Get the specific updated group with full details
            const groupResponse = await apiClient.getGroup(updatedGroupId)
            const updatedGroup = groupResponse.group

            // Update the groups list
            setGroups(prevGroups => {
                const index = prevGroups.findIndex(g => g.id === updatedGroupId)
                if (index >= 0) {
                    const newGroups = [...prevGroups]
                    newGroups[index] = updatedGroup
                    return newGroups
                }
                return prevGroups
            })

            // Update the selected group if it's the one that was modified
            if (selectedGroup && selectedGroup.id === updatedGroupId) {
                setSelectedGroup(updatedGroup)
            }
        } catch (err) {
            console.error('Failed to refresh group data:', err)
            // Fallback: refresh all groups
            try {
                const groupsResponse = await apiClient.getGroups()
                setGroups(groupsResponse.groups || [])
            } catch (fallbackErr) {
                console.error('Failed to refresh all groups:', fallbackErr)
            }
        }
    }

    return (
        <div className={`${styles.groupsContainer} ${selectedGroup ? styles.split : ''}`}>
            <div className={`${styles.splitView} ${selectedGroup ? styles.split : ''}`}>
                {/* Main Groups Content */}
                <div className={selectedGroup ? styles.groupsSidebar : styles.groupsMain}>
                    {!selectedGroup && (
                        <div className={styles.header}>
                            <div className={styles.headerLeft}>
                                <h2>Groups</h2>
                                <p>Manage team groups and permissions</p>
                            </div>
                            <button 
                                className={styles.addButton}
                                onClick={() => setShowAddModal(true)}
                            >
                                + Add Group
                            </button>
                        </div>
                    )}

                    {/* Conditional Content: Show full table or simple list based on selection */}
                    {!selectedGroup ? (
                        <>
                            {/* Search and Filters */}
                            <div className={styles.controls}>
                                <select
                                    value={leaderFilter}
                                    onChange={(e) => setLeaderFilter(e.target.value)}
                                    className={styles.filterSelect}
                                >
                                    <option value="all">All Leaders</option>
                                    {leaders.map(leader => (
                                        <option key={leader.id} value={leader.id}>
                                            {leader.name}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className={styles.filterSelect}
                                >
                                    <option value="name">Sort by Name</option>
                                    <option value="member_count">Sort by Members</option>
                                    <option value="created_at">Sort by Date</option>
                                    <option value="leader_name">Sort by Leader</option>
                                </select>
                                <div className={styles.searchBox}>
                                    <input
                                        type="text"
                                        placeholder="Search groups..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className={`${styles.searchInput} color-text-primary`}
                                    />
                                    <span className={styles.searchIcon}>🔍</span>
                                </div>
                                <button 
                                    className={styles.sortOrderButton}
                                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                                    title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                                >
                                    {sortOrder === 'asc' ? '↑' : '↓'}
                                </button>
                            </div>

                            {/* Groups Table */}
                            <div className={styles.tableContainer}>
                                <table className={styles.groupsTable}>
                                    <thead>
                                        <tr>
                                            <th>Group Name</th>
                                            <th>Leader</th>
                                            <th>Members</th>
                                            <th>Created</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="5" className={`${styles.noResults} color-text-primary`}>
                                                    Loading groups...
                                                </td>
                                            </tr>
                                        ) : error ? (
                                            <tr>
                                                <td colSpan="5" className={`${styles.noResults} color-text-primary`}>
                                                    {error}
                                                </td>
                                            </tr>
                                        ) : filteredGroups.length > 0 ? (
                                            filteredGroups.map(group => (
                                                <tr 
                                                    key={group.id}
                                                    onClick={() => handleGroupSelect(group)}
                                                    className={styles.tableRow}
                                                >
                                                    <td>
                                                        <div className={styles.groupName}>
                                                            <span className={`${styles.name} color-text-primary`}>{group.name}</span>
                                                            <span className={`${styles.description} color-text-primary`}>{group.description || 'No description'}</span>
                                                        </div>
                                                    </td>
                                                    <td className="color-text-primary">{group.created_by?.name || 'Unknown'}</td>
                                                    <td className="color-text-primary">{group.member_count}</td>
                                                    <td className="color-text-primary">{formatDate(group.created_at)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className={`${styles.noResults} color-text-primary`}>
                                                    No groups found matching your criteria
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        /* Simple Groups List for Split View */
                        <div className={styles.groupsListContainer}>
                            <div className={styles.listHeader}>
                                <h3>Groups</h3>
                                <div className={styles.listHeaderRight}>
                                    <span className={styles.groupCount}>{filteredGroups.length} groups</span>
                                    <button 
                                        className={styles.addButtonCompact}
                                        onClick={() => setShowAddModal(true)}
                                        aria-label="Add Group"
                                        title="Add Group"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                            <div className={styles.groupsList}>
                                {filteredGroups.map(group => (
                                    <div 
                                        key={group.id}
                                        className={`${styles.groupListItem} ${group.id === selectedGroup.id ? styles.active : ''}`}
                                        onClick={() => handleGroupSelect(group)}
                                    >
                                        <div className={`${styles.groupListName} color-text-primary`}>{group.name}</div>
                                        <div className={styles.groupListInfo}>
                                            <span className={`${styles.groupListMembers} color-text-primary`}>{group.member_count} members</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Group Details Panel */}
                {selectedGroup && (
                    <GroupDetails
                        key={selectedGroup.id}
                        group={selectedGroup}
                        onClose={handleCloseDetails}
                        onArchive={handleArchiveGroup}
                        onGroupUpdate={handleGroupUpdate}
                        apiToken={apiToken}
                        onSuccessMessage={(message) => console.log('Success:', message)}
                        onError={(error) => console.error('Error:', error)}
                    />
                )}
            </div>

            {/* Add Group Modal */}
            <AddGroupModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSave={handleGroupCreated}
                apiToken={apiToken}
            />
        </div>
    )
}